import React from 'react';
import { CheckCircle2, Award } from 'lucide-react';

/**
 * "Trusted By 1000+ Australian Small Businesses Since 2016" module
 * Sits directly under the hero/cover on Home and all internal pages.
 */
const TrustedByModule = () => {
  const features = [
    'High-Volume Keywords',
    'Verifiable Rankings',
    'Monthly Content Creation',
    'Premium Backlinks',
    'Transparent Reporting',
  ];

  return (
    <section
      className="relative bg-[#0f172a] text-white overflow-hidden"
      data-testid="trusted-by-module"
    >
      {/* Top alert bar */}
      <a
        href="#real-brands-real-results"
        onClick={(e) => {
          const target = document.getElementById('real-brands-real-results');
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
        className="block bg-gradient-to-r from-[#ec4899] via-[#db2777] to-[#ec4899] text-white text-center py-3 px-4 text-sm md:text-base font-bold tracking-wide hover:brightness-110 transition-all"
        data-testid="see-real-client-rankings-below"
      >
        <span className="inline-flex items-center gap-2">
          <Award size={18} className="text-amber-300" />
          See Real Client Rankings Below
          <span className="animate-bounce inline-block">↓</span>
        </span>
      </a>

      <div className="container mx-auto px-6 md:px-12 py-16 md:py-20 max-w-6xl">
        {/* Headline */}
        <h2 className="font-['Outfit'] text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white text-center md:text-left mb-14">
          Trusted By{' '}
          <span className="text-[#22c55e]">1000+</span>{' '}
          Australian Small Businesses{' '}
          <span className="text-[#22c55e]">Since 2016</span>
        </h2>

        {/* Badge chip */}
        <div className="flex justify-center mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-800/80 border border-slate-700 px-5 py-2 text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-amber-300">
            <span aria-hidden="true">◎</span>
            HIGH-VOLUME KEYWORDS. REAL RESULTS.
            <span aria-hidden="true">◎</span>
          </span>
        </div>

        {/* Main hero statement */}
        <div className="text-center max-w-4xl mx-auto">
          <h3 className="font-['Outfit'] text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-8">
            We rank you for{' '}
            <span className="block md:inline text-[#22c55e]">high search volume keywords</span>
          </h3>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-4">
            No games. No gimmicks. No targeting long-tail low-value keywords to inflate reports.
          </p>
          <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-10">
            We go after the keywords that actually drive traffic, leads, and revenue. See our client results below.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-3 md:gap-x-4">
            {features.map((feature, i) => (
              <React.Fragment key={feature}>
                <span className="inline-flex items-center gap-2 text-sm md:text-base text-slate-200">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#22c55e]">
                    <CheckCircle2 size={12} className="text-slate-900" strokeWidth={3} />
                  </span>
                  {feature}
                </span>
                {i < features.length - 1 && (
                  <span className="hidden md:inline text-slate-600">|</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedByModule;
